import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, LineChart, RefreshCw } from "lucide-react";
import { getMarket } from "@/lib/api";

function Sparkline({ data, up }) {
  if (!data || data.length < 2) return null;
  const w = 96, h = 32;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const pts = data
    .map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / range) * h}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="overflow-visible">
      <polyline
        points={pts}
        fill="none"
        strokeWidth="1.5"
        stroke={up ? "#34D399" : "#EF4444"}
      />
    </svg>
  );
}

export default function PriceTracker() {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    getMarket(10)
      .then((d) => setCoins(d.coins || []))
      .catch((e) => console.error("Failed to load market data:", e))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="markets" className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-ember">
            Live Markets
          </span>
          <h2 className="mt-4 text-3xl sm:text-4xl font-display font-bold tracking-tight flex items-center gap-3">
            <LineChart className="w-7 h-7 text-ember" />
            The Crypto Control Room
          </h2>
        </div>
        <button
          onClick={load}
          data-testid="market-refresh-btn"
          className="inline-flex items-center gap-2 border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-wide hover:border-ember hover:text-ember transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="border border-white/10 bg-card/50 overflow-x-auto" data-testid="market-table">
        <table className="w-full font-mono text-sm min-w-[640px]">
          <thead>
            <tr className="text-left text-muted-foreground border-b border-white/10 text-xs uppercase tracking-wider">
              <th className="px-5 py-4">#</th>
              <th className="px-5 py-4">Asset</th>
              <th className="px-5 py-4 text-right">Price</th>
              <th className="px-5 py-4 text-right">24h</th>
              <th className="px-5 py-4 text-right hidden sm:table-cell">Market Cap</th>
              <th className="px-5 py-4 text-right hidden md:table-cell">7d</th>
            </tr>
          </thead>
          <tbody>
            {coins.map((c, i) => {
              const up = (c.price_change_percentage_24h ?? 0) >= 0;
              return (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.03 }}
                  className="border-b border-white/5 hover:bg-white/5 transition-colors"
                  data-testid={`market-row-${c.symbol}`}
                >
                  <td className="px-5 py-4 text-muted-foreground">{i + 1}</td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      {c.image && <img src={c.image} alt={c.name} className="w-6 h-6" />}
                      <span className="font-semibold">{c.name}</span>
                      <span className="text-muted-foreground uppercase">{c.symbol}</span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    ${c.current_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                  </td>
                  <td className={`px-5 py-4 text-right ${up ? "text-emerald-400" : "text-red-400"}`}>
                    <span className="inline-flex items-center gap-1 justify-end">
                      {up ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
                      {Math.abs(c.price_change_percentage_24h ?? 0).toFixed(2)}%
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right text-muted-foreground hidden sm:table-cell">
                    ${(c.market_cap || 0).toLocaleString()}
                  </td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <div className="flex justify-end">
                      <Sparkline data={c.sparkline} up={up} />
                    </div>
                  </td>
                </motion.tr>
              );
            })}
            {!loading && coins.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-muted-foreground">
                  Market data unavailable right now.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <p className="mt-4 font-mono text-xs text-muted-foreground">
        Live data via CoinGecko · refreshes every 60s · for reference only, not financial advice.
      </p>
    </section>
  );
}
